import type { CatalogMessageId } from '~/i18n';
import { at, evenPlanes, sameShape, templateOfRow } from '../layout/layout';
import {
    DatasetRow,
    ImageLayer,
    Layout,
    Plane,
    planeContent,
    RowTemplate,
} from '../layout/types';

/**
 * Named hanging protocols: a protocol is a row shape, applied to every
 * dataset row. Applying one rebuilds the grid for the current datasets — a
 * reproducible, nameable layout rather than ad-hoc tiling. Each entry is
 * data, so a new one needs no new code path.
 */
export interface HangingProtocol {
    /** The protocol's identity, as the grid records the one in force. */
    readonly name: string;
    /** The message the layout menu names the protocol by. */
    readonly labelId: CatalogMessageId;
    readonly template: RowTemplate;
}

/** Planes a tri-planar row hangs, in reading order. */
const DEFAULT_BIND_PLANES: ReadonlyArray<Plane> = [
    Plane.Transversal,
    Plane.Sagittal,
    Plane.Coronal,
];

/**
 * The clinical tri-planar hanging: a full-height transversal beside a stacked
 * sagittal and coronal. The transversal carries the contouring work, so it gets
 * the area; the reformats are there to confirm the level.
 */
export const TRIPLANAR: RowTemplate = {
    columnPercents: [50, 50],
    rowPercents: [50, 50],
    panes: [
        {
            content: planeContent(Plane.Transversal),
            placement: { colStart: 1, colSpan: 1, rowStart: 1, rowSpan: 2 },
        },
        { content: planeContent(Plane.Sagittal), placement: at(2, 1) },
        { content: planeContent(Plane.Coronal), placement: at(2, 2) },
    ],
};

/** Three planes at equal width, one row of tracks. */
export const TRIPLANAR_EVEN: RowTemplate = evenPlanes(DEFAULT_BIND_PLANES);

/**
 * The row's own image beside its fused secondary, unblended, both on one
 * plane. Both panes view one Environment, so they scroll together through
 * the registration that placed the secondary. A row with nothing fused
 * draws its primary beside an empty pane.
 */
export function sideBySide(plane: Plane): RowTemplate {
    return {
        columnPercents: [50, 50],
        rowPercents: [100],
        panes: [
            {
                content: planeContent(plane, ImageLayer.Primary),
                placement: at(1, 1),
            },
            {
                content: planeContent(plane, ImageLayer.Secondary),
                placement: at(2, 1),
            },
        ],
    };
}

/** The layout menu's side by side, on the transversal plane the contouring uses. */
export const SIDE_BY_SIDE: RowTemplate = sideBySide(Plane.Transversal);

/** A row with no panes: the placeholder a picker binds a dataset into. */
export const EMPTY_ROW: RowTemplate = {
    columnPercents: [],
    rowPercents: [],
    panes: [],
};

/** The shape a freshly-bound row gets when none is chosen. */
export const DEFAULT_ROW_TEMPLATE: RowTemplate = TRIPLANAR;

/** The layout menu: each entry names a row shape the user can apply. */
export const HANGING_PROTOCOLS: ReadonlyArray<HangingProtocol> = [
    { name: 'Tri-planar', labelId: 'rowLayout.triPlanar', template: TRIPLANAR },
    {
        name: 'Tri-planar even',
        labelId: 'rowLayout.triPlanarEven',
        template: TRIPLANAR_EVEN,
    },
    {
        name: 'Side by side',
        labelId: 'rowLayout.sideBySide',
        template: SIDE_BY_SIDE,
    },
    {
        name: 'Transversal',
        labelId: 'rowLayout.transversal',
        template: evenPlanes([Plane.Transversal]),
    },
    {
        name: 'Transversal / Sagittal',
        labelId: 'rowLayout.transversalSagittal',
        template: evenPlanes([Plane.Transversal, Plane.Sagittal]),
    },
];

/** The protocol a grid hangs when nothing else is chosen. */
export const DEFAULT_HANGING_PROTOCOL: HangingProtocol = HANGING_PROTOCOLS[0];

/**
 * The protocol of that name, or {@link DEFAULT_HANGING_PROTOCOL} when no
 * entry carries it. A stored name therefore survives an entry being renamed
 * or retired without hanging a shape the table no longer defines.
 */
export function hangingProtocolByName(name: string): HangingProtocol {
    return (
        HANGING_PROTOCOLS.find((protocol) => protocol.name === name) ??
        DEFAULT_HANGING_PROTOCOL
    );
}

/**
 * The protocol that names a shape, or `undefined` when no entry names it.
 * Track sizes do not count: a shape the user has resized still has its
 * protocol.
 */
export function protocolOfTemplate(
    template: RowTemplate
): HangingProtocol | undefined {
    return HANGING_PROTOCOLS.find((protocol) =>
        sameShape(template, protocol.template)
    );
}

/**
 * The protocol one row hangs, or `undefined` when it hangs a shape no entry
 * names. Neither track sizes nor the band of panels a row hangs under its
 * image panes count.
 */
export function protocolOfRow(row: DatasetRow): HangingProtocol | undefined {
    return protocolOfTemplate(templateOfRow(row));
}

/**
 * The protocol every bound row hangs, or `undefined` when the rows differ
 * from each other, a row hangs a shape no entry names, or no row is bound.
 */
export function protocolOfLayout(layout: Layout): HangingProtocol | undefined {
    const bound = layout.rows.filter((row) => row.dataset !== undefined);
    if (bound.length === 0) {
        return undefined;
    }
    const [first, ...rest] = bound.map(protocolOfRow);
    return first !== undefined && rest.every((protocol) => protocol === first)
        ? first
        : undefined;
}
